import type { ReactNode } from 'react';
import { Wordmark } from '../../ui';
import { useApplyTheme } from '../session';

/**
 * THE WINDOW WITHOUT A TITLE (E3, `spec/screens/login.md` section 2 item 1).
 *
 * The credential screens are the one place in this product that renders OUTSIDE the shell:
 * no sidebar, no toolbar, no large title - the window BODY is the canvas and one form
 * stands on it (kit: Apple macOS 27 UI Kit.sketch / `Windows/Light/Default without Title/
 * Active` and its Dark twin). The ground is `--color-canvas`, which the shell round already
 * points at `--color-window-background` at and above the desktop step and at the iOS grouped
 * background below it (tokens.css, the s1-shell block at the end of the file), so this screen
 * takes the kit's window fill without stating a colour of its own.
 *
 * ONE COLUMN AT 360px (`--auth-form-width`), the width the spec derives from the kit's Lg
 * text field plus a label column. At 1440 the column is centred in the window; at 375 it
 * stands from the top under the safe area, because a form whose keyboard covers half the
 * viewport should not also be vertically centred (section 3 item 1). Nothing here is
 * positioned `fixed`: the keyboard scrolls the page and the button comes up with it.
 *
 * IT APPLIES THE THEME ITSELF. `/login`, `/register` and the three recovery screens live
 * outside the AppShell, and the shell is what puts `dark` on <html> - so a screen that
 * forgets this hook renders light for somebody whose whole OS is dark.
 *
 * THE MARK LEADS. This is the one screen a person sees before they are inside the product,
 * so it is the one screen that says whose product it is; the heading says what the screen
 * DOES. The mark is one step under the Title 1 heading (Title 2 on the desktop, 19px on the
 * phone): it is the brand, not the headline (`DESIGN.md` Brand, "the full wordmark appears
 * in the sidebar header and on the login screen").
 *
 * THE PROPS ARE A CONTRACT WITH THREE OTHER SCREENS. `ForgotPasswordPage`, `ResetPasswordPage`
 * and `AcceptInvitationPage` are Parent F's and render through this layout; `title` and
 * `subtitle` stay exactly as they were so F's round keeps working and inherits the
 * construction.
 */
export interface AuthLayoutProps {
  readonly title: string;
  readonly subtitle: string;
  readonly children: ReactNode;
  /**
   * The screen is the page's one landmark, except inside `/dev/auth-states`, which renders
   * whole screens as specimens inside its own `<main>` - and two mains is not a document.
   */
  readonly landmark?: boolean | undefined;
}

export function AuthLayout({ title, subtitle, children, landmark = true }: AuthLayoutProps) {
  useApplyTheme();
  const Element = landmark ? 'main' : 'div';

  return (
    <Element
      className="page-gutter flex min-h-dvh flex-col items-center justify-start bg-canvas md:justify-center"
      /*
        THE SAFE AREA IS PART OF THE PADDING rather than a second box: `env()` resolves to 0
        on a desktop browser, so the same declaration is the phone's notch inset and the
        desktop's plain space. It is inline because it is one calc per edge and this screen
        owns no stylesheet - `index.css` belongs to the shell.
      */
      style={{
        paddingBlockStart: 'calc(var(--space-12) + env(safe-area-inset-top))',
        paddingBlockEnd: 'calc(var(--space-12) + env(safe-area-inset-bottom))',
      }}
    >
      <div className="w-full max-w-(--auth-form-width) space-y-8">
        <div className="space-y-4">
          <Wordmark className="text-md md:text-lg" />
          <div className="space-y-1">
            {/* Title 1, the spec's own token for this heading (section 2 item 3). */}
            <h1 className="text-xl font-semibold text-ink">{title}</h1>
            <p className="text-sm text-ink-muted">{subtitle}</p>
          </div>
        </div>
        {children}
      </div>
    </Element>
  );
}
